/**
 * AdminDashboard Presenter — 管理端接口调用封装
 *
 * MVP 架构中的 Presenter 层，负责：
 * 1. 封装所有管理端相关的 API 调用（postHttp / getHttp）
 * 2. 将回调式 API 包装为 Promise，方便 async/await 使用
 * 3. 适配接口返回字段与 UI 层期望字段的差异
 *
 * 接口文档参考：根目录 接口文档.md
 * 路由前缀：
 *   - FXZ  = /fg-ai-center/fxzapi  （用户、AI工具、公告）
 *   - NEWAPI = /fg-ai-center/newapi  （渠道模型、Token 统计）
 */

import { postHttp, getHttp } from '../../utils/service';
import { FXZ_API_URI_PREFIX, NEWAPI_API_URI_PREFIX } from '../../utils/fxzApiService';

// ===================== NewAPI 接口（渠道模型 & Token 统计） =====================

/**
 * 获取渠道可用模型列表
 * 接口文档 #14 — POST /newapi/channel/models
 * @returns {Promise<{status: number, msg: string, data: string[]}>}
 */
export function getModels() {
  return new Promise((resolve) => {
    postHttp(NEWAPI_API_URI_PREFIX, '/channel/models', {}, resolve);
  });
}

/**
 * 查询模型维度的用户 Token 用量
 * 接口文档 #16 — POST /newapi/log/model_token_usage
 * @param {object} params - { start_time: number, end_time: number, model_name?: string }
 * @returns {Promise<{status: number, msg: string, data: Array}>}
 */
export function getModelTokenUsage(params) {
  return new Promise((resolve) => {
    postHttp(NEWAPI_API_URI_PREFIX, '/log/model_token_usage', params, resolve);
  });
}

/**
 * 查询用户总 Token 和各模型明细
 * 接口文档 #17 — POST /newapi/log/user_model_token_usage
 * @param {object} params - { start_time: number, end_time: number }
 * @returns {Promise<{status: number, msg: string, data: Array}>}
 */
export function getUserModelTokenUsage(params) {
  return new Promise((resolve) => {
    postHttp(NEWAPI_API_URI_PREFIX, '/log/user_model_token_usage', params, resolve);
  });
}

/**
 * 查询渠道模型 Token 用量
 * 接口文档 #18 — POST /newapi/log/channel_model_token_usage
 * @param {object} params - { start_time: number, end_time: number }
 * @returns {Promise<{status: number, msg: string, data: Array}>}
 */
export function getChannelModelTokenUsage(params) {
  return new Promise((resolve) => {
    postHttp(NEWAPI_API_URI_PREFIX, '/log/channel_model_token_usage', params, resolve);
  });
}

// ===================== 用户管理接口 =====================

/**
 * 获取所有用户列表
 * 接口文档 #4 — GET /fxzapi/user/list
 * ⚠️ 数据适配：接口返回 { data: [...] }，UI 层期望 { users: [...] }
 * @returns {Promise<{status: number, msg: string, users: Array}>}
 */
export function getUsers() {
  return new Promise((resolve) => {
    getHttp(FXZ_API_URI_PREFIX, '/user/list', {}, (res) => {
      // 适配：接口返回 data 字段 → UI 层使用 users 字段
      resolve({ ...res, users: res?.data || [] });
    });
  });
}

export function getPortalPresence() {
  return new Promise(resolve => {
    getHttp(FXZ_API_URI_PREFIX, '/user/portal_presence', {}, resolve);
  });
}

/**
 * 管理员新增用户
 * 接口文档 #3 — POST /fxzapi/user/add
 * @param {object} params - { username: string, email: string, pwd: string, is_admin: boolean }
 * @returns {Promise<{status: number, msg: string, user_id: string}>}
 */
export function addUser(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/user/add', params, resolve);
  });
}

/**
 * 删除用户
 * 接口文档 #7 — POST /fxzapi/user/del/{user_id}
 * ⚠️ 特殊路由：user_id 拼接到 URL 路径中
 * @param {string} userId - 要删除的用户 ID
 * @returns {Promise<{status: number, msg: string}>}
 */
export function deleteUser(userId) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, `/user/del/${userId}`, {}, resolve);
  });
}

/**
 * 更新用户密码
 * 接口文档 #5 — POST /fxzapi/user/update_pwd
 * @param {object} params - { user_id: string, pwd: string }
 * @returns {Promise<{status: number, msg: string}>}
 */
export function updateUserPwd(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/user/update_pwd', params, resolve);
  });
}

/**
 * 设置/清除用户独立短用户名（OIDC preferred_username）
 * POST /fxzapi/user/update_username_alias
 * @param {object} params - { user_id: string, username_alias: string | null }
 * @returns {Promise<{status: number, msg: string}>}
 */
export function updateUsernameAlias(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/user/update_username_alias', params, resolve);
  });
}

/**
 * 一键重置所有非管理员密码（后台任务）
 * POST /fxzapi/user/reset_non_admin_pwd
 * @returns {Promise<{status: number, msg: string, data?: {running: boolean}}>}
 */
export function resetNonAdminPasswords() {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/user/reset_non_admin_pwd', {}, resolve);
  });
}

/**
 * 查询批量重置密码任务状态
 * GET /fxzapi/user/reset_non_admin_pwd/status
 * @returns {Promise<{status: number, data?: {running, reset_count, admin_skipped_count, failed_count, failed}}>}
 */
export function getResetNonAdminPwdStatus() {
  return new Promise((resolve) => {
    getHttp(FXZ_API_URI_PREFIX, '/user/reset_non_admin_pwd/status', {}, resolve);
  });
}

// ===================== AI 工具管理接口 =====================

/**
 * 查询 AI 工具列表
 * 接口文档 #7 — POST /fxzapi/agent/qry
 * @param {object} params - { agent_name?: string, agent_id?: string, agent_type?: string, status?: string }
 * @returns {Promise<{status: number, msg: string, agents: Array}>}
 */
export function getAgents(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/agent/qry', params || {}, resolve);
  });
}

/**
 * 新增 AI 工具
 * 接口文档 #8 — POST /fxzapi/agent/add
 * @param {object} params - { agent_name, agent_tags?, agent_type?, agent_des?, agent_jump_url?, is_support_email?, status?, dep_ids, role_ids }
 * @returns {Promise<{status: number, msg: string, agent_id: string}>}
 */
export function addAgent(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/agent/add', params, resolve);
  });
}

/**
 * 更新 AI 工具
 * 接口文档 #9 — POST /fxzapi/agent/update
 * @param {object} params - { agent_id, agent_name?, agent_tags?, agent_type?, agent_des?, agent_jump_url?, is_support_email?, status? }
 * @returns {Promise<{status: number, msg: string}>}
 */
export function updateAgent(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/agent/update', params, resolve);
  });
}

/**
 * 删除 AI 工具
 * 接口文档 #10 — POST /fxzapi/agent/delete
 * @param {object} params - { agent_id: string }
 * @returns {Promise<{status: number, msg: string}>}
 */
export function deleteAgent(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/agent/delete', params, resolve);
  });
}

// ===================== 公告管理接口 =====================

/**
 * 查询公告列表
 * 接口文档 #11 — POST /fxzapi/public_statement/qry
 * @returns {Promise<{status: number, msg: string, public_statements: Array}>}
 */
export function getAnnouncements() {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/public_statement/qry', {}, resolve);
  });
}

/**
 * 新增公告
 * 接口文档 #12 — POST /fxzapi/public_statement/add
 * @param {object} params - { public_content: string }
 * @returns {Promise<{status: number, msg: string, id: number}>}
 */
export function addAnnouncement(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/public_statement/add', params, resolve);
  });
}

/**
 * 删除公告
 * 接口文档 #13 — POST /fxzapi/public_statement/delete
 * @param {object} params - { id: number }
 * @returns {Promise<{status: number, msg: string}>}
 */
export function deleteAnnouncement(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/public_statement/delete', params, resolve);
  });
}

/**
 * 编辑公告（内容 + 发布时间）
 * POST /fxzapi/public_statement/update
 * @param {object} params - { id: number, public_content?: string, created_time?: string }
 * @returns {Promise<{status: number, msg: string}>}
 */
export function updateAnnouncement(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/public_statement/update', params, resolve);
  });
}
